import { Box, Grid, Text } from '@simplywallst/ui-core';

import { Slider } from '../../Slider';
import {
  calculateFairValueBreakdown,
  getCompanyFinancialData,
} from '../../../utils/calculation';

import type { CalculationOutput } from '../../../utils/calculation';
import type { NarrativeValuationCompany } from '../../../hooks/useNarrativeValuationCompany';
import { tooltipContent } from '../tooltipContent';
import { INPUT_VALUE_RANGES } from '../../../utils/validation';
import { LabelledValue } from './LabelledValue';
import {
  VALUATOR_RATE_PRECISION,
  type NarrativeForm,
} from '@/pages/PublicNarrativeAuthoring/utils/schema';
import { NarrativeValuatorTooltip } from './NarrativeValuatorTooltip';

export type Props = {
  company: NarrativeValuationCompany;
  onChange: (data: NarrativeForm) => void;
  values: NarrativeForm;
};

export const CalculationBreakdown = ({ company, onChange, values }: Props) => {
  const { discountRatePercentage, priceEarningsMultiple } = values;
  const companyFinancialData = getCompanyFinancialData(company);

  const breakdown: CalculationOutput = calculateFairValueBreakdown(
    company,
    values
  );
  const { formatted } = breakdown;

  const {
    fairValue,
    futureMarketCap,
    futureEarnings,
    futureYear,
    currentYear,
    sharePriceFutureYear,
    futureSharesOutstanding,
  } = formatted;

  const handleOnChange = (type: string) => (value: number) => {
    onChange({
      ...values,
      [type]: value,
    });
  };

  return (
    <Grid gap="16px">
      {/* Risk level section */}
      <Grid gap="8px">
        <Box padding="x2">
          <Grid templateColumns="auto min-content" alignItems="center">
            <Text typography="xSmallStrong" color="textSolid">
              Risk Level (Discount Rate)
            </Text>
            <NarrativeValuatorTooltip>
              {tooltipContent.discountRate}
            </NarrativeValuatorTooltip>
          </Grid>
          <Text typography="xSmall" color="textSoft">
            Our default considers factors like the company’s size, volatility,
            profitablity and country of operation.
          </Text>
        </Box>
        <Box padding="x2">
          <Box marginBottom="x4">
            <Slider
              identifier="narrative-valuator-discount-rate-percentage"
              minRange={INPUT_VALUE_RANGES.discountRatePercentage.min}
              maxRange={INPUT_VALUE_RANGES.discountRatePercentage.max}
              value={discountRatePercentage}
              precision={VALUATOR_RATE_PRECISION}
              onChange={handleOnChange('discountRatePercentage')}
              suffix="%"
              bands={[
                {
                  threshold: INPUT_VALUE_RANGES.discountRatePercentage.min,
                  label: 'Decrease',
                  align: 'left',
                },
                {
                  threshold: INPUT_VALUE_RANGES.discountRatePercentage.max,
                  label: 'Increase',
                  align: 'right',
                },
              ]}
            />
          </Box>
          <LabelledValue label="Current discount rate">
            {companyFinancialData.discountRatePercentage.toFixed(2)}%
          </LabelledValue>
        </Box>
      </Grid>

      {/* Calculation section */}
      <Grid gap="16px">
        <Grid
          templateColumns="auto min-content"
          alignItems="center"
          paddingX="x2"
        >
          <Text typography="xSmallStrong" color="textSolid">
            Calculation
          </Text>
          <NarrativeValuatorTooltip>
            {tooltipContent.calculationBreakdown}
          </NarrativeValuatorTooltip>
        </Grid>

        {/* First tile */}
        {/* Earnings 2029 X PE Ratio 2029 = Market Cap 2029 */}
        <Box backgroundColor="spaceGray" padding="x2">
          <Grid templateColumns="auto 1fr auto 1fr auto">
            <Grid templateRows="max-content max-content" placeContent="center">
              <Text as="span" typography="tiny" color="chart02">
                {futureEarnings}
              </Text>
              <Text as="span" typography="tiny" color="textSolid">
                Earnings {futureYear}
              </Text>
            </Grid>

            {/* Operator Symbol */}
            <Grid placeContent="center">
              <Box
                backgroundColor="noir"
                borderRadius="x2"
                height={16}
                width={16}
              >
                <Grid placeContent="center" height={16} width={16}>
                  <Text as="span" color="textSolid">
                    x
                  </Text>
                </Grid>
              </Box>
            </Grid>

            <Grid templateRows="max-content max-content" placeContent="center">
              <Text as="span" typography="tiny" color="chart03">
                {priceEarningsMultiple.toFixed(2)}x
              </Text>
              <Text as="span" typography="tiny" color="textSolid">
                PE Ratio {futureYear}
              </Text>
            </Grid>

            {/* Operator Symbol */}
            <Grid placeContent="center">
              <Box
                backgroundColor="noir"
                borderRadius="x2"
                height={16}
                width={16}
              >
                <Grid placeContent="center" height={16} width={16}>
                  <Text as="span" color="textSolid">
                    =
                  </Text>
                </Grid>
              </Box>
            </Grid>

            <Grid templateRows="max-content max-content" placeContent="center">
              <Text as="span" typography="tiny" color="chart04">
                {futureMarketCap}
              </Text>
              <Text as="span" typography="tiny" color="textSolid">
                Market Cap {futureYear}
              </Text>
            </Grid>
          </Grid>
        </Box>

        {/* Second tile */}
        {/* Market Cap 2029 / Number of Shares Outstanding 2029 = Share Price 2029 */}
        <Box backgroundColor="spaceGray" padding="x2">
          <Grid templateColumns="auto 1fr auto 1fr auto">
            <Grid templateRows="max-content max-content" placeContent="center">
              <Text as="span" typography="tiny" color="chart04">
                {futureMarketCap}
              </Text>
              <Text as="span" typography="tiny" color="textSolid">
                Market Cap {futureYear}
              </Text>
            </Grid>

            {/* Operator Symbol */}
            <Grid placeContent="center">
              <Box
                backgroundColor="noir"
                borderRadius="x2"
                height={16}
                width={16}
              >
                <Grid placeContent="center" height={16} width={16}>
                  <Text as="span" color="textSolid">
                    /
                  </Text>
                </Grid>
              </Box>
            </Grid>

            <Grid templateRows="max-content max-content" placeContent="center">
              <Text as="span" typography="tiny" color="textSofter">
                {futureSharesOutstanding}
              </Text>
              <Text as="span" typography="tiny" color="textSolid">
                No. shares {futureYear}
              </Text>
            </Grid>

            {/* Operator Symbol */}
            <Grid placeContent="center">
              <Box
                backgroundColor="noir"
                borderRadius="x2"
                height={16}
                width={16}
              >
                <Grid placeContent="center" height={16} width={16}>
                  <Text as="span" color="textSolid">
                    =
                  </Text>
                </Grid>
              </Box>
            </Grid>

            <Grid templateRows="max-content max-content" placeContent="center">
              <Text as="span" typography="tiny" color="chart04">
                {sharePriceFutureYear}
              </Text>
              <Text as="span" typography="tiny" color="textSolid">
                Share Price {futureYear}
              </Text>
            </Grid>
          </Grid>
        </Box>

        {/* Third tile */}
        {/* 2029 Share Price (Discounted Back @ DR) = Fair Value Estimate 2024 */}
        <Box
          backgroundColor="spaceGray"
          padding="x2"
          borderBottomLeftRadius="x1"
          borderBottomRightRadius="x1"
        >
          <Grid templateColumns="auto 1fr auto">
            <Grid templateRows="max-content max-content" placeContent="center">
              <div>
                <Text as="span" typography="tiny" color="chart04">
                  {sharePriceFutureYear}
                </Text>

                <Text as="span" typography="tiny" color="textSolid">
                  {' '}
                  Share Price {futureYear}
                </Text>
              </div>
              <Text as="span" typography="tiny" color="textSofter">
                Discounted to {new Date().getFullYear()} @{' '}
                {discountRatePercentage.toFixed(2)}% p.a.
              </Text>
            </Grid>

            {/* Operator Symbol */}
            <Grid placeContent="center">
              <Box
                backgroundColor="noir"
                borderRadius="x2"
                height={16}
                width={16}
              >
                <Grid placeContent="center" height={16} width={16}>
                  <Text as="span" color="textSolid">
                    =
                  </Text>
                </Grid>
              </Box>
            </Grid>

            <Grid templateRows="max-content max-content" placeContent="center">
              <Text as="span" typography="tiny" color="chart01">
                {fairValue}
              </Text>
              <Text as="span" typography="tiny" color="textSolid">
                Fair Value {currentYear}
              </Text>
            </Grid>
          </Grid>
        </Box>
      </Grid>
    </Grid>
  );
};
